import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogTitle, IconButton, List, ListItem, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ETextureOrigin } from "../../@types/ContextProviderRelated";
import { EAssetType, IFilesApiColor, IFilesApiMaterial, TTexture } from "../../@types/FilesAPIModels";
import { loadMaterialTexture, loadProductMaterialTexture } from "../../utils/textureLoaderUtility";

interface MaterialBoxProps {
  asset: IFilesApiMaterial;
  origin: ETextureOrigin;
  productId?: number;
}

const MaterialBox: React.FC<MaterialBoxProps> = ({ asset, origin, productId }) => {
  const [src, setSrc] = useState<string | undefined>(
    asset.previewPictureUrl && asset.previewPictureUrl.trim() !== ""
      ? asset.previewPictureUrl
      : undefined
  );

  useEffect(() => {
    if (!src) {
      const loader =
        origin === ETextureOrigin.PRODUCT && productId !== undefined  && productId !== null
          ? () => loadProductMaterialTexture(productId, asset.id)
          : () => loadMaterialTexture(asset.id);

      loader()
        .then((texture) => {
          if (texture?.image) {
            const img = texture.image as HTMLImageElement;
            setSrc(img.src || "/img/placeholder.jpg");
          } else {
            setSrc("/img/placeholder.jpg");
          }
        })
        .catch((err) => {
          console.error("Error loading material texture for modal preview:", err);
          setSrc("/img/placeholder.jpg");
        });
    }
  }, [asset, src, origin, productId]);

  return (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        backgroundImage: src ? `url(${src})` : "none",
        backgroundColor: src ? "transparent" : "grey.300",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
  );
};

interface ColorBoxProps {
  asset: IFilesApiColor;
}

const ColorBox: React.FC<ColorBoxProps> = ({ asset }) => (
  <Box
    sx={{
      width: 56,
      height: 56,
      borderRadius: "50%",
      backgroundColor: asset.hex,
    }}
  />
);

interface AssetSelectionModalProps {
  open: boolean;
  title: string;
  assets: TTexture[];
  onClose: () => void;
  onSelect: (asset: TTexture | null) => void;
  origin: ETextureOrigin;
  productId?: number;
}

const AssetSelectionModal: React.FC<AssetSelectionModalProps> = ({
  open,
  title,
  assets,
  onClose,
  onSelect,
  origin,
  productId,
}) => (
  <Dialog
    open={open}
    onClose={(event, reason) => {
      // console.log("Dialog onClose triggered, reason:", reason);
      onClose();
    }}
  >
    <DialogTitle sx={{ m: 0, p: 2 }}>
      {title}
      <IconButton
        aria-label="close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <List sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
      {assets.map((asset) => (
        <ListItem
          button
          key={asset.id}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(asset);
          }}
          sx={{
            width: 100,
            flexDirection: "column",
            alignItems: "center",
            padding: 1,
          }}
        >
          <Box>
            {asset.assetType === EAssetType.MATERIAL ? (
              <MaterialBox asset={asset as IFilesApiMaterial} origin={origin} productId={productId} />
            ) : (
              <ColorBox asset={asset as IFilesApiColor} />
            )}
          </Box>
          <Typography variant="caption" align="center" sx={{ marginTop: 1 }}>
            {asset.name}
          </Typography>
        </ListItem>
      ))}
    </List>
  </Dialog>
);

export default AssetSelectionModal;
